<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { sectionsApi, type FormSection } from '@/services/api/sections';
import { useNotifier } from '@/composables/notifier';

const route = useRoute();
const notify = useNotifier();
const section = ref<FormSection | null>(null);
const submitting = ref(false);

function ids(): { projectId: string; formId: string; sectionId: string } {
  const p = route.params.id;
  const f = route.params.form_id;
  const s = route.params.section_id;
  return {
    projectId: Array.isArray(p) ? (p[0] ?? '') : p ?? '',
    formId: Array.isArray(f) ? (f[0] ?? '') : f ?? '',
    sectionId: Array.isArray(s) ? (s[0] ?? '') : s ?? '',
  };
}

async function reload(): Promise<void> {
  const { projectId, formId, sectionId } = ids();
  section.value = await sectionsApi.get(projectId, formId, sectionId);
}

async function save(): Promise<void> {
  if (!section.value || submitting.value) return;
  submitting.value = true;
  try {
    const { projectId, formId } = ids();
    section.value = await sectionsApi.update(
      projectId,
      formId,
      section.value as FormSection & { id: number },
    );
    notify.info('Section saved');
  } finally {
    submitting.value = false;
  }
}

onMounted(reload);
</script>

<template>
  <section v-if="section" class="resource">
    <h1>Edit Section</h1>
    <form class="resource__form" @submit.prevent="save">
      <label><span>Name</span><input v-model="section.name" type="text" required /></label>
      <label><span>Display Name</span><input v-model="section.display_name" type="text" /></label>
      <button type="submit" :disabled="submitting">Save Section</button>
    </form>
    <p class="resource__note">
      Controls editing is part of task #34 (form-control directives port).
    </p>
  </section>
  <p v-else class="resource">Loading…</p>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
<style scoped>
.resource__form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 32rem;
}
.resource__form input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}
.resource__form button {
  align-self: flex-start;
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.375rem;
  cursor: pointer;
}
.resource__note {
  margin-top: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}
</style>
